import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accesApi, type Profil, type RoleAcces } from '../../services/acces-api';
import {
  Bascule,
  Bloc,
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Etiquette,
  Saisie,
  Section,
  Selection,
  ValeurCalculee,
} from '../../components/ndf/ui/Champ';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { useToast } from '../../context/ToastContext';

/**
 * Profils et rôles — le « qui a le droit de quoi » de Gertrude.
 *
 * Un profil se définit MODULE PAR MODULE : chaque module déclare ses actions,
 * et cet écran en dessine les cases. Ajouter un module ne demande donc aucune
 * modification ici.
 */

const PORTEES: Array<{ valeur: RoleAcces['portee']; libelle: string; aide: string }> = [
  { valeur: 'soi', libelle: 'Ses propres données', aide: 'Ne voit que ses enregistrements.' },
  {
    valeur: 'equipe',
    libelle: 'Son équipe',
    aide: 'Voit aussi ceux de ses collaborateurs, et de ceux de ses managers.',
  },
  { valeur: 'entreprise', libelle: 'Toute l’entreprise', aide: 'Voit tout, en lecture.' },
];

export function ProfilsRolesPage() {
  const [onglet, setOnglet] = useState<'profils' | 'roles'>('profils');

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-4">
        <h1 className="font-titre text-xl font-bold text-nuit">Profils et rôles</h1>
        <p className="text-sm text-nuit/70">
          Le profil dit ce qu’une personne peut faire ; le rôle, sur quels enregistrements.
        </p>
      </header>

      <nav className="mb-4 flex flex-wrap gap-2">
        {(['profils', 'roles'] as const).map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => setOnglet(o)}
            className={`min-h-11 rounded-lg px-3 text-sm font-medium transition-all ${
              onglet === o
                ? 'bg-nuit text-white shadow-sm'
                : 'bg-white text-nuit ring-1 ring-nuit/15 hover:ring-nuit/30'
            }`}
          >
            {o === 'profils' ? 'Profils' : 'Rôles'}
          </button>
        ))}
      </nav>

      {onglet === 'profils' ? <OngletProfils /> : <OngletRoles />}
    </div>
  );
}

function OngletProfils() {
  const qc = useQueryClient();
  const toast = useToast();
  const [edition, setEdition] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Record<string, string[]>>({});
  const [nouveau, setNouveau] = useState('');

  const { data: modulesData } = useQuery({ queryKey: ['acces', 'modules'], queryFn: () => accesApi.modules() });
  const { data, isLoading, error } = useQuery({
    queryKey: ['acces', 'profils'],
    queryFn: () => accesApi.profils.liste(),
    retry: false,
  });

  const modules = (modulesData?.modules ?? []).filter((m) => m.actions.length > 0);
  const profils = data?.profils ?? [];
  const invalider = () => qc.invalidateQueries({ queryKey: ['acces'] });

  const enregistrer = useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: Record<string, string[]> }) =>
      accesApi.profils.modifier(id, { permissions }),
    onSuccess: () => {
      toast.success('Profil enregistré.');
      setEdition(null);
      setBrouillon({});
      invalider();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  const creer = useMutation({
    mutationFn: (nom: string) => accesApi.profils.creer({ nom }),
    onSuccess: () => {
      toast.success('Profil créé. Cochez ses droits, puis enregistrez.');
      setNouveau('');
      invalider();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La création a échoué.'),
  });

  const basculer = useMutation({
    mutationFn: ({ id, actif }: { id: string; actif: boolean }) => accesApi.profils.modifier(id, { actif }),
    onSuccess: () => invalider(),
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La modification a échoué.'),
  });

  return (
    <Section
      titre="Profils"
      teinte="vert"
      description="Un profil se définit une fois et s’attribue à plusieurs personnes : corriger des droits se fait alors à un seul endroit."
    >
      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <div className="space-y-3">
        {profils.map((p) => {
          const enEdition = edition === p.id;
          const permissions = enEdition ? { ...p.permissions, ...brouillon } : p.permissions;
          return (
            <Bloc
              key={p.id}
              titre={p.nom}
              teinte={p.systeme ? 'ciel' : 'violet'}
              inactif={!p.actif}
              actions={
                <div className="flex items-center gap-2">
                  <Etiquette>
                    {p.nb_utilisateurs} utilisateur{p.nb_utilisateurs > 1 ? 's' : ''}
                  </Etiquette>
                  {!p.systeme && (
                    <Bascule
                      actif={p.actif}
                      libelleActif="Actif"
                      libelleInactif="Inactif"
                      onChange={(actif) => basculer.mutate({ id: p.id, actif })}
                    />
                  )}
                </div>
              }
            >
              <div className="space-y-3 border-t border-nuit/10 p-3">
                {p.description && <p className="text-sm text-nuit/70">{p.description}</p>}

                {modules.map((m) => {
                  const actives = permissions[m.code] ?? [];
                  return (
                    <div key={m.code} className="rounded-md border border-nuit/10 p-3">
                      <h4 className="font-titre text-sm font-bold text-nuit">{m.libelle}</h4>
                      {m.description && <p className="text-xs text-nuit/60">{m.description}</p>}
                      <div className="mt-2 grid gap-x-6 sm:grid-cols-2">
                        {m.actions.map((a) =>
                          enEdition ? (
                            <Case
                              key={a.code}
                              label={a.libelle}
                              aide={a.aide}
                              checked={actives.includes(a.code)}
                              onChange={(e) => {
                                const set = new Set(actives);
                                if (e.target.checked) set.add(a.code);
                                else set.delete(a.code);
                                setBrouillon((b) => ({ ...b, [m.code]: [...set] }));
                              }}
                            />
                          ) : (
                            <p key={a.code} className="py-1 text-sm">
                              <span className={actives.includes(a.code) ? 'text-vert' : 'text-nuit/35'}>
                                {actives.includes(a.code) ? '✓' : '·'} {a.libelle}
                              </span>
                            </p>
                          ),
                        )}
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-end gap-2">
                  {!enEdition ? (
                    <BoutonSecondaire
                      type="button"
                      onClick={() => {
                        setEdition(p.id);
                        setBrouillon({});
                      }}
                    >
                      Modifier les droits
                    </BoutonSecondaire>
                  ) : (
                    <>
                      <BoutonSecondaire
                        type="button"
                        onClick={() => {
                          setEdition(null);
                          setBrouillon({});
                        }}
                      >
                        Annuler
                      </BoutonSecondaire>
                      <BoutonPrincipal
                        type="button"
                        disabled={enregistrer.isPending}
                        onClick={() =>
                          enregistrer.mutate({ id: p.id, permissions: { ...p.permissions, ...brouillon } })
                        }
                      >
                        {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
                      </BoutonPrincipal>
                    </>
                  )}
                </div>
              </div>
            </Bloc>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-nuit/25 p-3">
        <Champ label="Nouveau profil">
          <Saisie
            value={nouveau}
            onChange={(e) => setNouveau(e.target.value)}
            placeholder="Par exemple : Contrôleur de gestion"
          />
        </Champ>
        <BoutonPrincipal
          type="button"
          disabled={!nouveau.trim() || creer.isPending}
          onClick={() => creer.mutate(nouveau.trim())}
        >
          Créer
        </BoutonPrincipal>
      </div>
    </Section>
  );
}

function OngletRoles() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: ['acces', 'roles'],
    queryFn: () => accesApi.roles.liste(),
    retry: false,
  });
  const [nouveau, setNouveau] = useState({ nom: '', portee: 'soi' as RoleAcces['portee'] });

  const creer = useMutation({
    mutationFn: () => accesApi.roles.creer(nouveau),
    onSuccess: () => {
      toast.success('Rôle créé.');
      setNouveau({ nom: '', portee: 'soi' });
      qc.invalidateQueries({ queryKey: ['acces'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La création a échoué.'),
  });

  return (
    <Section
      titre="Rôles"
      teinte="ciel"
      description="Le périmètre de ce qu’une personne voit. Élargir la vue n’accorde jamais un droit de modification."
    >
      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <div className="space-y-3">
        {(data?.roles ?? []).map((r) => (
          <Bloc
            key={r.id}
            titre={r.nom}
            teinte="ciel"
            inactif={!r.actif}
            actions={
              <Etiquette>
                {r.nb_utilisateurs} utilisateur{r.nb_utilisateurs > 1 ? 's' : ''}
              </Etiquette>
            }
          >
            <div className="border-t border-nuit/10 p-3 text-sm text-nuit/70">
              {PORTEES.find((p) => p.valeur === r.portee)?.aide ?? r.portee}
            </div>
          </Bloc>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-nuit/25 p-3">
        <Champ label="Nouveau rôle">
          <Saisie
            value={nouveau.nom}
            onChange={(e) => setNouveau((n) => ({ ...n, nom: e.target.value }))}
            placeholder="Par exemple : Direction"
          />
        </Champ>
        <Champ label="Périmètre">
          <Selection
            value={nouveau.portee}
            onChange={(e) => setNouveau((n) => ({ ...n, portee: e.target.value as RoleAcces['portee'] }))}
          >
            {PORTEES.map((p) => (
              <option key={p.valeur} value={p.valeur}>
                {p.libelle}
              </option>
            ))}
          </Selection>
        </Champ>
        <BoutonPrincipal
          type="button"
          disabled={!nouveau.nom.trim() || creer.isPending}
          onClick={() => creer.mutate()}
        >
          Créer
        </BoutonPrincipal>
      </div>
    </Section>
  );
}
